import { JobSeekingStatus } from "@nimble/core-client/types";
import { usePostHog } from "posthog-js/react";
import { useMemo } from "react";

import type { formMode } from "@/components/auth/form-mode";
import type { AuthAudience } from "@/lib/constants/auth-audiences";
import type { AuthMethod } from "@/lib/constants/auth-methods";
import { SetPersonFn, StorageService } from "@/lib/posthog/nps-eligibility";
import {
  CaptureEventFn,
  createTrackingEngine,
  MatchAcceptedSource,
} from "@/lib/posthog/track";

export function useTracking() {
  const posthog = usePostHog();

  return useMemo(() => {
    const browserStorageFn: StorageService = {
      get: (key) =>
        typeof window !== "undefined" ? localStorage.getItem(key) : null,
      set: (key, val) => {
        if (typeof window !== "undefined") localStorage.setItem(key, val);
      },
    };

    const captureWithLogFn: CaptureEventFn = (event, props) => {
      if (posthog) {
        posthog.capture(event, props);
      }
    };

    const setPersonFn: SetPersonFn = (props) => {
      if (posthog) {
        posthog.people.set(props);
      }
    };

    const trackingEngine = createTrackingEngine(
      browserStorageFn,
      captureWithLogFn,
      setPersonFn,
    );

    return {
      trackExpressInterestSubmitted: (props: {
        schoolNcesId: string;
        schoolName: string;
      }) => {
        trackingEngine.trackExpressInterestSubmitted({
          school_nces_id: props.schoolNcesId,
          school_name: props.schoolName,
        });
      },

      trackMatchAccepted: (props: {
        schoolNcesId: string;
        schoolName: string;
        source: MatchAcceptedSource;
      }) => {
        trackingEngine.trackMatchAccepted({
          school_nces_id: props.schoolNcesId,
          school_name: props.schoolName,
          source: props.source,
        });
      },

      trackAuthSubmitted: (props: {
        authMethod: AuthMethod;
        sharedDevice: boolean;
        mode: formMode;
        audience: AuthAudience;
      }) => {
        trackingEngine.trackAuthSubmitted({
          auth_method: props.authMethod,
          shared_device: props.sharedDevice,
          mode: props.mode,
          audience: props.audience,
        });
      },

      trackJobViewed: (props: {
        jobId: string;
        schoolNcesId: string;
        districtId: string;
        state: string;
      }) => {
        trackingEngine.trackJobViewed({
          job_id: props.jobId,
          school_nces_id: props.schoolNcesId,
          district_id: props.districtId,
          state: props.state,
        });
      },

      trackQuickApplyClicked: (props: { jobId: string }) => {
        trackingEngine.trackQuickApplyClicked({
          job_id: props.jobId,
        });
      },

      trackExternalApplyClicked: (props: { jobId: string; jobUrl: string }) => {
        trackingEngine.trackExternalApplyClicked({
          job_id: props.jobId,
          job_url: props.jobUrl,
        });
      },

      trackSalaryScheduleClicked: (props: {
        schoolNcesId: string;
        salaryScheduleUrl: string;
      }) => {
        trackingEngine.trackSalaryScheduleClicked({
          school_nces_id: props.schoolNcesId,
          salary_schedule_url: props.salaryScheduleUrl,
        });
      },

      trackJobSeekingStatusChanged: (props: {
        previousJobSeekingStatus: JobSeekingStatus | null;
        newJobSeekingStatus: JobSeekingStatus;
      }) => {
        trackingEngine.trackJobSeekingStatusChanged({
          previous_job_seeking_status: props.previousJobSeekingStatus,
          new_job_seeking_status: props.newJobSeekingStatus,
        });
      },
    };
  }, [posthog]);
}
